import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ClinicianTerms from "@/components/ClinicianTerms";
import { Spinner } from "@/components/ui-kit";

export default function ReacceptGate({ children }) {
  const [status, setStatus] = useState(null);
  const [legal, setLegal] = useState(null);
  const [v, setV] = useState({ a1: false, a2: false, b: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/pro/legal/status").then((r) => {
      setStatus(r.data);
      setV((x) => ({ ...x, a2: !!r.data.marketing_opt_in }));
      if (r.data.required) api.get("/legal/clinician").then((l) => setLegal(l.data));
    }).catch(() => setStatus({ required: false }));
  }, []);
  if (!status?.required) return children;
  const accept = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post("/pro/legal/accept", { version: legal.version, hash: legal.hash, accept_terms: v.a1, accept_declarations: v.b, marketing_opt_in: v.a2 });
      toast.success("Thank you — your acceptance has been recorded. A copy is in Settings.");
      setStatus({ ...status, required: false });
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not record acceptance — please check your connection and try again.";
      setErr(msg);
      toast.error(msg);
      // Agreement text changed since it was loaded: reload the latest version so they can accept it.
      if (e.response?.status === 409) {
        const l = await api.get("/legal/clinician").catch(() => null);
        if (l) { setLegal(l.data); setV((x) => ({ ...x, a1: false, b: false })); }
      }
    }
    setBusy(false);
  };
  return (
    <>
      {children}
      <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-3" data-testid="reaccept-gate">
        <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-5 sm:p-6 shadow-xl">
          <h2 className="text-lg font-bold text-slate-900">Please review the Clinician Agreement</h2>
          <p className="mt-1 text-[12.5px] text-slate-500">
            {status.accepted_version ? `A new version (${status.current_version}) has been published.` : `Version ${status.current_version} must be accepted to continue.`} New Clinician Orders can't be assigned to you until you accept.
          </p>
          {legal ? <ClinicianTerms legal={legal} value={v} onChange={setV} /> : <div className="py-6 grid place-items-center"><Spinner className="w-5 h-5" /></div>}
          {err && <p className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12.5px] text-rose-700" data-testid="reaccept-error">{err}</p>}
          <button type="button" onClick={accept} disabled={!legal || !v.a1 || !v.b || busy} data-testid="reaccept-submit"
            style={{ background: "#2A5298", borderColor: "#2A5298", color: "#fff" }}
            className="pq-btn w-full py-3 mt-3 text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? <Spinner className="w-4 h-4" /> : <>Accept and Continue <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </>
  );
}
